import { useEffect, useState } from 'react';
import { canvasWorkingLabels, summarizeCanvasProgress, type CanvasProgressInput } from './canvas-progress-summary';

/** Disclosure state belongs to the reader, even while new details arrive. */
export function ThinkingBlock({ msgId, progress, isStreaming }: { msgId: string; progress: CanvasProgressInput; isStreaming: boolean }) {
  const [open, setOpen] = useState(false);
  const text = summarizeCanvasProgress(progress, isStreaming).join('\n');
  return <div data-testid={`classifier-reasoning-${msgId}`} style={{ marginBottom: 12 }}>
    <button aria-expanded={open} aria-controls={`thinking-details-${msgId}`} onClick={() => setOpen(value => !value)}
      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', color: '#777b80', font: 'inherit', fontSize: 13, fontWeight: 500 }}>
      <span aria-hidden="true" style={{ display: 'inline-block', fontSize: 10, transform: open ? 'rotate(90deg)' : 'none' }}>▶</span>
      {isStreaming ? 'Thinking details' : 'Work details'}
    </button>
    {open && <div id={`thinking-details-${msgId}`} style={{ padding: '8px 0 0 16px', fontSize: 13, color: '#777b80', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text}</div>}
  </div>;
}

/** Live project state is authoritative; saved unfinished cards are history. */
export function BottomStatusLine({ isTyping, progress = {} }: { isTyping: boolean; progress?: CanvasProgressInput }) {
  if (!isTyping) return null;
  return <RotatingStatus progress={progress} />;
}

function RotatingStatus({ progress }: { progress: CanvasProgressInput }) {
  const labels = canvasWorkingLabels(progress);
  const stageKey = labels.join('|');
  const [rotation, setRotation] = useState({ stageKey, index: 0 });
  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let timer: ReturnType<typeof setInterval> | undefined;
    const restart = () => {
      if (timer) clearInterval(timer);
      setRotation({ stageKey, index: 0 });
      if (!motion.matches && labels.length > 1) timer = setInterval(() => setRotation(current => ({ stageKey, index: (current.index + 1) % labels.length })), 3000);
    };
    restart();
    motion.addEventListener('change', restart);
    return () => { if (timer) clearInterval(timer); motion.removeEventListener('change', restart); };
  }, [stageKey]);
  const label = labels[rotation.stageKey === stageKey ? rotation.index : 0] ?? labels[0];
  return <div role="status" data-testid="chat-working-status" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, color: '#777b80' }}>
    <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: '#A04F6A' }} />
    <span aria-hidden="true">{label}…</span>
    <span className="sr-only">{labels[0]}.</span>
  </div>;
}
